import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const QUERY_KEY = 'document-validation';

export function useDocumentValidation<TPayload, TResult>({
    documentId,
    validate,
    debounceMs = 400,
}: {
    documentId: string;
    validate: (payload: TPayload, signal: AbortSignal) => Promise<TResult>;
    debounceMs?: number;
}) {
    const queryClient = useQueryClient();
    const [payload, setPayload] = useState<TPayload | null>(null);
    const [debouncedPayload, setDebouncedPayload] = useState<TPayload | null>(null);
    const [seeded, setSeeded] = useState<TResult | null>(null);

    useEffect(() => {
        if (payload === null) {
            setDebouncedPayload(null);
            return;
        }
        const timer = setTimeout(() => setDebouncedPayload(payload), debounceMs);
        return () => clearTimeout(timer);
    }, [payload, debounceMs]);

    useEffect(() => {
        setPayload(null);
        setDebouncedPayload(null);
        setSeeded(null);
    }, [documentId]);

    const query = useQuery({
        queryKey: [QUERY_KEY, documentId, debouncedPayload],
        queryFn: ({ signal }) => validate(debouncedPayload as TPayload, signal),
        enabled: debouncedPayload !== null,
        staleTime: Infinity,
    });

    const seedResult = useCallback((result: TResult) => {
        void queryClient.cancelQueries({ queryKey: [QUERY_KEY, documentId] });
        setPayload(null);
        setDebouncedPayload(null);
        setSeeded(result);
    }, [queryClient, documentId]);

    const requestValidation = useCallback((next: TPayload) => {
        setSeeded(null);
        setPayload(next);
    }, []);

    const isPending = payload !== debouncedPayload;

    return {
        result: isPending ? null : (seeded ?? query.data ?? null),
        isChecking: seeded === null && (isPending || query.isFetching),
        isAvailable: isPending || !query.isError,
        seedResult,
        requestValidation,
    };
}
